import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { runGated } from "@/lib/adsGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { TEST_IDS } from "@/constants/testIds";
import { motion } from "framer-motion";
import { Send, Sparkles } from "lucide-react";

const SUGGESTIONS = [
  "What should I wear today?",
  "What matches this shirt?",
  "Which colour suits me?",
  "Plan a date-night look.",
  "What should I buy next?",
];

export default function Stylist() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const endRef = useRef();

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setSending(true);
    try {
      const { data } = await runGated("chat_messages", () => api.post("/stylist/chat", {
        message: msg,
        session_id: sessionId,
      }));
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch {
      setMessages((m) => [...m, { role: "assistant", content: "I had trouble responding. Please try again." }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 lg:py-16">
      <div className="flex items-baseline justify-between">
        <div>
          <p className="eyebrow flex items-center gap-2"><Sparkles size={12} /> Your personal stylist</p>
          <h1 className="editorial-h1 mt-3">Stylebay.</h1>
        </div>
      </div>

      <div className="mt-10 border border-border bg-card min-h-[60vh] flex flex-col">
        <div className="flex-1 overflow-y-auto p-6 lg:p-10 space-y-6">
          {messages.length === 0 && (
            <div className="text-center py-16">
              <p className="font-serif text-3xl">How can I dress you today?</p>
              <p className="text-sm text-muted-foreground mt-3">Ask anything — colour theory, what suits you, daily outfit, gifts.</p>
              <div className="mt-8 flex flex-wrap gap-2 justify-center">
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    onClick={() => send(s)}
                    className="px-3 py-2 border border-border text-xs hover:border-foreground transition"
                  >{s}</button>
                ))}
              </div>
            </div>
          )}
          {messages.map((m, i) => (
            <motion.div
              key={i}
              data-testid={TEST_IDS.stylist.message(i)}
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[80%] p-4 ${
                  m.role === "user"
                    ? "bg-accent text-accent-foreground"
                    : "bg-muted text-foreground border border-border"
                }`}
              >
                <p className="text-sm whitespace-pre-wrap leading-relaxed">{m.content}</p>
              </div>
            </motion.div>
          ))}
          {sending && (
            <div className="flex justify-start">
              <div className="bg-muted border border-border p-4">
                <span className="inline-flex gap-1">
                  <span className="h-1.5 w-1.5 rounded-full bg-accent animate-bounce" style={{ animationDelay: "0ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-accent animate-bounce" style={{ animationDelay: "120ms" }} />
                  <span className="h-1.5 w-1.5 rounded-full bg-accent animate-bounce" style={{ animationDelay: "240ms" }} />
                </span>
              </div>
            </div>
          )}
          <div ref={endRef} />
        </div>
        <div className="border-t border-border p-4 flex gap-2">
          <Input
            data-testid={TEST_IDS.stylist.input}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && send()}
            placeholder="Ask Stylebay…"
            className="rounded-none h-12"
          />
          <Button
            data-testid={TEST_IDS.stylist.sendBtn}
            onClick={() => send()}
            disabled={sending}
            className="rounded-none h-12 w-12 p-0"
          >
            <Send size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
